import { messageCustomTypes } from '@inbox-demo-nx/constants';
import { Reaction } from '@inbox-demo-nx/types';
import { delay } from '@inbox-demo-nx/utils';
import type emoji from 'node-emoji/lib/emoji.json';

import { chatAxios } from './axios';

type MessageType = 'MESG' | 'FILE' | 'ADMM';
type Message = {
  message_id: number;
  type: MessageType;
  created_at: number;
};

export async function sendMessage({
  channelUrl,
  params,
}: {
  channelUrl: string;
  params: {
    message_type: MessageType;
    user_id?: string;
    message?: string;
    custom_type?: string;
    data?: string;
    url?: string;
    thumbnails?: any[];
    file_name?: string;
    file_type?: string;
    created_at?: number;
    translations?: string[];
  };
}) {
  const { data } = await chatAxios.post<Message>(
    `/v3/group_channels/${channelUrl}/messages`,
    params
  );
  return data;
}

export async function sendBotMessage({
  channelUrl,
  botUserId,
  message,
  customType,
  data: messageData,
  markAsRead,
  createdAt,
}: {
  channelUrl: string;
  botUserId: string;
  message: string;
  customType?: string;
  data?: string;
  markAsRead?: boolean;
  createdAt?: number;
}) {
  const { data } = await chatAxios.post(`/v3/bots/${botUserId}/send`, {
    channel_url: channelUrl,
    message,
    custom_type: customType,
    data: messageData,
    mark_as_read: markAsRead,
    created_at: createdAt,
  });
  return data;
}

export async function editMessage({
  channelUrl,
  messageId,
  params,
}: {
  channelUrl: string;
  messageId: number;
  params: {
    message_type: MessageType;
    message?: string;
    data?: string;
    created_at?: number;
    custom_type?: string;
  };
}) {
  const { data } = await chatAxios.put(
    `/v3/group_channels/${channelUrl}/messages/${messageId}`,
    params
  );
  return data;
}

export async function deleteMessage(channelUrl: string, messageId: number) {
  return chatAxios.delete(
    `/v3/group_channels/${channelUrl}/messages/${messageId}`
  );
}

export async function getMessage(channelUrl: string, messageId: number) {
  const { data } = await chatAxios.get(
    `/v3/group_channels/${channelUrl}/messages/${messageId}`
  );
  return data;
}

export async function fetchMessages({
  channelUrl,
  customTypes,
  messageType,
  prevLimit,
  nextLimit,
  messageId,
  senderId,
  include,
}: {
  channelUrl: string;
  customTypes?: string;
  messageType?: MessageType;
  prevLimit?: number;
  nextLimit?: number;
  messageId?: number;
  senderId?: string;
  include?: boolean;
}) {
  const { data } = await chatAxios.get(
    `/v3/group_channels/${channelUrl}/messages`,
    {
      params: {
        custom_types: customTypes,
        message_ts: messageId ? undefined : Date.now(),
        message_id: messageId,
        include,
        message_type: messageType,
        sender_id: senderId,
        prev_limit: prevLimit,
        next_limit: nextLimit,
      },
    }
  );
  return data.messages;
}

export async function markAsRead({
  userId,
  channelUrl,
  timestamp,
}: {
  userId: string;
  channelUrl: string;
  timestamp?: number;
}) {
  const { data } = await chatAxios.put(
    `/v3/group_channels/${channelUrl}/messages/mark_as_read`,
    { user_id: userId, timestamp }
  );
  return data;
}

export async function addReaction({
  channelUrl,
  messageId,
  userId,
  reaction,
}: {
  channelUrl: string;
  messageId: number | string;
  userId: string;
  reaction: keyof typeof emoji;
}) {
  const { data } = await chatAxios.post(
    `/v3/group_channels/${channelUrl}/messages/${messageId}/reactions`,
    {
      user_id: userId,
      reaction,
    }
  );
  return data;
}

export const addReactionsToMessage = async ({
  channelUrl,
  messageId,
  reactions,
}: {
  channelUrl: string;
  messageId: number;
  reactions?: Reaction[];
}) => {
  return Promise.all(
    reactions?.map(({ emoji, userIds }) =>
      userIds.map((userId) =>
        addReaction({
          channelUrl,
          messageId,
          reaction: emoji,
          userId,
        })
      )
    ) ?? []
  );
};

export async function translateMessage({
  channelUrl,
  messageId,
  targetLanguages,
}: {
  channelUrl: string;
  messageId: number;
  targetLanguages: string[];
}) {
  const { data: message } = await chatAxios.post(
    `/v3/group_channels/${channelUrl}/messages/${messageId}/translation`,
    { target_langs: targetLanguages }
  );
  return message;
}

export async function sendMessageAndTranslate(
  ...params: Parameters<typeof sendMessage>
) {
  const [{ channelUrl, params: options }] = params;
  const message = await sendMessage({
    channelUrl,
    params: {
      ...options,
      translations: ['es', 'ko', 'en'],
    },
  });
  return message;
}

export const typeAndSendMessage = async ({
  channelUrl,
  userId,
  typingDuration = 1000,
  messageParams,
}: {
  channelUrl: string;
  userId: string;
  typingDuration?: number;
  messageParams: Parameters<typeof sendMessageAndTranslate>[0]['params'];
}) => {
  markAsRead({ userId, channelUrl });

  if (typingDuration === 0 || messageParams.message_type === 'FILE') {
    return sendMessageAndTranslate({ channelUrl, params: messageParams });
  }

  const { message_id: messageId } = await sendMessageAndTranslate({
    channelUrl,
    params: {
      message_type: 'MESG',
      custom_type: messageCustomTypes.typingIndicator,
      user_id: userId,
      message: 'Typing...',
      created_at: messageParams.created_at,
    },
  });
  await delay(typingDuration);

  return editMessage({
    channelUrl,
    messageId,
    params: {
      ...messageParams,
      custom_type: messageParams.custom_type || '',
    },
  });
};
